// IMP-151 AC-2: rayteams local server (localhost:8008) 호출 추상화.
//
// 역할:
//   - baseURL / timeout 캡슐화 + 응답 unwrap + error sanitize.
//   - SmartDentAction.js 의 인라인 axios.get 호출 5회를 client 함수 호출로 대체.
//   - IMP-151 후속에서 PcActivationAction.js 모듈 분리 시 본 client 가 그대로 재사용.
//
// 주의:
//   - rayteams local server 는 무인증 (PR #214 SEC-007 참조). 신뢰 경계 너머 가정 필수.
//   - rayteams API 응답이 { status:"success", data:{...} } 로 래핑되는 경우 unwrap.
//   - getFile API 는 비래핑 응답이라 unwrap 안전 fallback 으로 원본 그대로 반환.
//   - 입력 검증 (path traversal / JSON.parse 가드 등) 은 호출자 책임.

import axios from 'axios';

const RAYTEAMS_BASE = 'http://localhost:8008';
// 데스크톱 미응답 시 UI 블로킹 방지 — 5초 timeout.
// rayteams local 호출은 보통 ms 단위라 여유분 충분.
const RAYTEAMS_TIMEOUT_MS = 5000;

// PR #216 Round 1 (Architecture #7): import 시점에 axios.create() 즉시 호출되면
// 본 모듈을 (직접/간접) import 하는 모든 테스트가 axios.create mock 을 추가해야 한다.
// SmartDentAction.js 를 import 하는 5개 테스트 파일이 영향을 받았다.
// → lazy singleton 으로 변경. 첫 함수 호출 시점에 한 번만 create.
let _client = null;
const getClient = () => {
    if (!_client) {
        _client = axios.create({
            baseURL: RAYTEAMS_BASE,
            timeout: RAYTEAMS_TIMEOUT_MS,
        });
    }
    return _client;
};

// 응답 unwrap — { status:"success", data:{...} } 형식 → inner data 추출.
// 비래핑 응답(getFile 등)은 원본 그대로 반환.
// pure function 으로 분리 — 단위 테스트 용이성 + 재사용.
//
// PR #216 Round 2 (#2): 의도 명문화 — "plain object 만 unwrap" 이 정책.
//   - rayteams API 의 래핑 응답은 항상 inner object (배열 반환 사례 미관측).
//   - JS 에서 array 도 typeof "object" 이므로 명시적 Array.isArray 가드로 배제.
//   - 미래에 배열 응답이 등장하면 (현 정책 위반) 원본 envelope 그대로 반환되어
//     호출자가 status 필드로 분기 가능. silent unwrap 보다 fail-loud 방향이 안전.
export const unwrapRayteamsResponse = (body) => {
    if (
        body?.status === 'success' &&
        body.data &&
        typeof body.data === 'object' &&
        !Array.isArray(body.data)
    ) {
        return body.data;
    }
    return body;
};

// PR #216 Round 1 (Security #5): axios error 객체는 url/headers/request 등을 포함한다.
// getFile 의 경우 URL 에 로컬 디스크 경로(C:/Ray/RAYTeams/raylink.json) 가 포함되고,
// 향후 SEC-007 에서 도입할 인증 헤더(CSRF 토큰, Authorization 등)도 error.config.headers
// 에 포함되어 같이 누출될 가능성이 있다. Sentry/console.error 로 raw error 가 로깅되면
// Sensitive Data Exposure (OWASP A04) 발생.
// → caller 가 catch 하여 로깅하더라도 url/headers/request 가 노출되지 않도록 sanitize.
const sanitizeError = (error) => {
    const sanitized = new Error(error?.message || 'Rayteams local error');
    sanitized.code = error?.code;
    sanitized.status = error?.response?.status;
    // 의도적으로 누락: config.url, config.headers, request, response.headers
    return sanitized;
};

// PR #216 Round 1 (Architecture #2, Code Quality #6): interceptor 가 unwrap 된
// inner data 를 반환하면 axios 의 표준 응답 객체({data, status, headers})를 폐기하고,
// 테스트에서도 mockGet 이 axios 표준 형태({data:{...}})와 다르게 직접 inner-only 객체를
// 반환하므로 interceptor 가 호출되지 않은 채 통과한다 (unwrap 로직이 production path 에서
// 실제로 검증되지 않음).
// → interceptor 제거하고 callGet 헬퍼에서 res.data 를 명시적으로 unwrap.
//   이렇게 하면 단위 테스트 mock 이 axios 표준 형태를 따라가게 되고, unwrap 로직이
//   공개 API (ping/getFile/getLicenseStatus 등) 호출을 통해 실제로 실행된다.
const callGet = async (path) => {
    try {
        const res = await getClient().get(path);
        return unwrapRayteamsResponse(res?.data);
    } catch (err) {
        throw sanitizeError(err);
    }
};

// 설치 확인 — /api/file/ping.
// 반환: { data:"pong", version:"..." } (응답 unwrap 적용).
export const ping = () => callGet('/api/file/ping');

// 파일 내용 조회 — /api/file/getFile/{path}.
// 주의: rayteams API 는 path 의 ':'/'/' 를 디코딩하지 않음. encodeURIComponent 미적용 (실측 검증).
// 호출자가 path traversal 방어 책임 (SEC-007 영역).
// 반환: 파일 JSON 객체 (비래핑 응답).
export const getFile = (path) => callGet(`/api/file/getFile/${path}`);

// PR #216 Round 1 (Architecture #4, Security #1):
// 기존 licenseCheck(appId, purpose) 는 read-only(check) 와 state-changing(use/revoke) 동작을
// 단일 함수에 합쳤고, purpose 를 검증 없이 URL 에 보간하여 query string injection 위험이 있었다.
// → purpose 별 3개 함수로 분할 + appId 는 encodeURIComponent 적용.
//   이로써 enum 이 시그니처에 강제되고, 미래 SEC-007 의 GET→POST 전환 시 state-changing
//   두 함수만 메서드 변경하면 된다.
//
// 라이선스 상태 조회/변경 — /api/license/check/{appId}?purpose={check|use|revoke}.
// 반환: { check, code, guid, sk, ... } (응답 unwrap 적용).
// 주의: purpose=use|revoke 가 GET 으로 상태 변경 — CSRF 위험 (SEC-007 추적).
const buildLicenseUrl = (appId, purpose) =>
    `/api/license/check/${encodeURIComponent(appId)}?purpose=${purpose}`;

// 현재 활성 상태 조회 (read-only).
export const getLicenseStatus = (appId) => callGet(buildLicenseUrl(appId, 'check'));

// 현재 PC 활성화 (state-changing).
export const activateLicense = (appId) => callGet(buildLicenseUrl(appId, 'use'));

// 현재 PC 비활성화 (state-changing).
export const revokeLicense = (appId) => callGet(buildLicenseUrl(appId, 'revoke'));

// IMP-175: PC 시스템 정보 조회 — Confluence "PC 권장 사항 체크" endpoint.
// 반환: { rtid, host: { totalMemory, cpuCount, ... }, cpuInfo: [...], gpuInfo: [...], ... }
//   (response unwrap 적용 — outer { status, data } 가 unwrap 되어 data 객체 반환).
// 호출자는 pcSpecChecker.checkPcSpec(response) 으로 권장사양 비교.
//
// PR #223 review #8 — `noneCheckDevice=true` 의미:
//   RAYTeams 측 device 등록 검증 우회 플래그. 본 endpoint 는 device 검증 없이 호출 가능하도록
//   RAYTeams API 설계상 의도된 SDK 호출 경로 (Confluence "Local License Check API doc" 참조).
//   응답에는 device 식별자(MAC/serial 등) 가 포함되지 않는 것이 RAYTeams 측 보안 가정.
//   향후 응답 스키마 확장 시 SmartDentAction.GetSystemSpecInfo 의 화이트리스트로 추가 PII drift 방어.
export const getSystemInfo = () =>
    callGet('/apps/api/rcm/getinfo?type=rt&noneCheckDevice=true');

// PR #216 Round 1 (Security #3): __internal__ = { client } 가 raw axios 인스턴스를
// 외부에 노출하면 interceptor/sanitize 우회 + 임의 path/메서드 호출 가능.
// → process.env.NODE_ENV === 'test' 일 때만 노출 (webpack DefinePlugin 으로 production
//   번들에서 환경 가드 분기가 dead code 로 제거됨 → tree-shaking 으로 production 노출 차단).
//   현재 단위 테스트는 unwrapRayteamsResponse + 공개 API 만 검증하면 충분하므로
//   본 export 는 통합 테스트 도입 시점까지 미사용 상태로 유지.
export const __internal__ =
    process.env.NODE_ENV === 'test' ? { getClient } : undefined;
